import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Page, StatTiles } from '../../components/shell/Page';
import SplitLayout, { paneForm } from '../../components/shell/SidePane';
import UserDevices from '../../components/users/UserDevices';
import { useResetPasswordPane } from '../../components/shell/ResetPasswordPane';
import { useAssignCoursesPane } from '../../components/batches/AssignCoursesPane';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { 
  Plus, 
  User, 
  Users,
  Trash, 
  PencilSimple, 
  Key, 
  ToggleRight,
  Upload,
  Download,
  Student,
  Chalkboard,
  Books
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function UserManagement() {
  const { api, user: currentUser, tenantInfo } = useAuth();
  const navigate = useNavigate();
  const isInstructor = currentUser?.role === 'instructor';
  const [users, setUsers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');

  // Side pane states (edit / bulk import). "New user" is a big form → its own tab (/admin/users/new)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bulkImportDialogOpen, setBulkImportDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [csvData, setCsvData] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Roles this organisation has defined, to offer on a user's record.
  const [customRoles, setCustomRoles] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        api.get('/users'),
        api.get('/users/stats'),
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      // Only a tenant admin has roles to hand out; a teacher opening this page does not.
      try {
        const rolesRes = await api.get('/roles');
        setCustomRoles(rolesRes.data || []);
      } catch {
        setCustomRoles([]);
      }
      // Activity logs might not be available for all roles
      try {
        const logsRes = await api.get('/activity-logs?limit=20');
        setActivityLogs(logsRes.data);
      } catch {
        setActivityLogs([]);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // "New user" opens the shared form in its own tab (pages/admin/UserForm.js)
  const openCreateUser = () => navigate('/admin/users/new');

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      await api.put(`/users/${selectedUser.id}`, {
        first_name: selectedUser.first_name,
        last_name: selectedUser.last_name,
        role: selectedUser.role,
        // "" puts them back on their plain built-in role.
        custom_role_id: selectedUser.custom_role_id || '',
        // -1 is how "go back to whatever their role allows" is sent: the update drops
        // nulls, so there would otherwise be no way to clear a per-person allowance.
        max_sessions: selectedUser.max_sessions === null || selectedUser.max_sessions === undefined
          ? -1 : selectedUser.max_sessions,
      });
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      const res = await api.put(`/users/${userId}/status`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Reset password → side pane (set password with eye icon / Generate, "require change at first login"), no alert()
  const resetPane = useResetPasswordPane({ api, onReset: fetchData, orgName: tenantInfo?.name });
  const assignPane = useAssignCoursesPane({ api, onDone: fetchData });

  const handleBulkImport = async () => {
    if (!csvData.trim()) {
      toast.error('Please enter CSV data');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/users/bulk-import', { csv_data: csvData });
      toast.success(`Imported ${res.data.created} users. ${res.data.failed.length} failed.`);
      if (res.data.failed.length > 0) {
        console.log('Failed imports:', res.data.failed);
        toast.error(`${res.data.failed.length} users failed to import. Check console for details.`);
      }
      setBulkImportDialogOpen(false);
      setCsvData('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import users');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = true;
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const learners = users.filter(u => u.role === 'learner');
  const instructors = users.filter(u => u.role === 'instructor');

  const downloadCSVTemplate = () => {
    const template = 'email,first_name,last_name,role,password\nstudent@example.com,John,Doe,learner,Welcome123!\ninstructor@example.com,Jane,Smith,instructor,Welcome123!';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_import_template.csv';
    a.click();
  };

  // ─── Side pane (small CRUD forms open here instead of popups) ───
  const closePane = () => { setEditDialogOpen(false); setBulkImportDialogOpen(false); resetPane.close(); assignPane.close(); };
  const openPane = (which) => { closePane(); which(true); };
  const openEdit = (u) => { setSelectedUser({ ...u }); openPane(setEditDialogOpen); };
  const openReset = (u) => { setEditDialogOpen(false); setBulkImportDialogOpen(false); assignPane.close(); resetPane.open(u); };
  const openAssign = (rows) => { setEditDialogOpen(false); setBulkImportDialogOpen(false); resetPane.close(); assignPane.open(rows); };
  let side = resetPane.side || assignPane.side;
  if (editDialogOpen && selectedUser) {
    const f = paneForm({ id: 'edit-user-form', onSubmit: handleUpdateUser, onCancel: closePane, submitLabel: 'Save changes', submitting, children: (
      <>
      <div className="pane-grid">
        <div className="field">
          <Label>First Name</Label>
          <Input
            value={selectedUser.first_name}
            onChange={(e) => setSelectedUser({...selectedUser, first_name: e.target.value})}
            required
          />
        </div>
        <div className="field">
          <Label>Last Name</Label>
          <Input
            value={selectedUser.last_name}
            onChange={(e) => setSelectedUser({...selectedUser, last_name: e.target.value})}
            required
          />
        </div>
      </div>
      <div className="field">
        <Label>Email</Label>
        <Input value={selectedUser.email || ''} disabled />
      </div>
      <div className="field">
        <Label>Role</Label>
        <Select
          value={selectedUser.role}
          onValueChange={(v) => setSelectedUser({...selectedUser, role: v})}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="learner">Learner</SelectItem>
            <SelectItem value="instructor">Instructor</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {customRoles.length > 0 && (
        <div className="field">
          <Label data-find="Role in this organisation">Role in this organisation</Label>
          <select
            className="form-select"
            value={selectedUser.custom_role_id || ''}
            onChange={(e) => setSelectedUser({ ...selectedUser, custom_role_id: e.target.value })}
            data-testid="user-custom-role"
          >
            <option value="">None — the plain built-in role</option>
            {customRoles
              .filter((r) => r.base_role === selectedUser.role)
              .map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <p className="field-hint">
            A role your organisation defined, narrowing what they can reach. Only roles
            built on their built-in role are offered.
          </p>
        </div>
      )}
      <UserDevices
        userId={selectedUser.id}
        value={selectedUser.max_sessions}
        onChange={(v) => setSelectedUser({ ...selectedUser, max_sessions: v })}
      />
      </>
    ) });
    side = { open: true, onOpenChange: closePane, title: `Edit: ${selectedUser.first_name} ${selectedUser.last_name}`, subtitle: 'Update user information', icon: PencilSimple, children: f.body, footer: f.footer, testid: 'edit-user-pane' };
  } else if (bulkImportDialogOpen) {
    const f = paneForm({ id: 'bulk-import-form', onSubmit: (e) => { e.preventDefault(); handleBulkImport(); }, onCancel: closePane, submitLabel: 'Import users', submitting, submitIcon: Upload, children: (
      <>
      <div className="flex justify-end">
        <Button type="button" variant="outline" size="sm" onClick={downloadCSVTemplate}>
          <Download className="w-4 h-4 mr-2" />
          Download Template
        </Button>
      </div>
      <div className="field">
        <Label>CSV Data</Label>
        <Textarea
          placeholder="email,first_name,last_name,role,password&#10;student@example.com,John,Doe,learner,Welcome123!"
          value={csvData}
          onChange={(e) => setCsvData(e.target.value)}
          rows={12}
          className="font-mono text-sm"
        />
        <p className="field-hint">
          Required columns: email, first_name, last_name, role (learner or instructor).
          Optional: password (auto-generated if empty)
        </p>
      </div>
      </>
    ) });
    side = { open: true, onOpenChange: closePane, title: 'Bulk import users', subtitle: 'Import multiple users from CSV data', icon: Upload, width: 44, children: f.body, footer: f.footer, testid: 'bulk-import-pane' };
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <SplitLayout side={side}>
      <Page data-testid="user-management-page">
        {stats && (
          <StatTiles items={[
            { label: 'Total users', value: stats.total_users, icon: Users },
            { label: 'Learners', value: stats.by_role?.learner || 0, icon: Student, iconClass: 'bg-sky-100 text-sky-700' },
            { label: 'Instructors', value: stats.by_role?.instructor || 0, icon: Chalkboard, iconClass: 'bg-violet-100 text-violet-700' },
            { label: 'Active', value: stats.active_users, icon: User, iconClass: 'bg-emerald-100 text-emerald-700' },
          ]} />
        )}

        <Tabs defaultValue="users" className="space-y-3">
          <TabsList>
            <TabsTrigger value="users">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="activity">Activity log</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <DataGrid
              title={isInstructor ? 'My Students' : 'Users'}
              rows={filteredUsers}
              loading={loading}
              onRefresh={fetchData}
              searchKeys={['email', 'phone', 'first_name', 'last_name']}
              headerExtra={!isInstructor && (
                <div className="inline-flex rounded-md border border-border overflow-hidden text-xs" data-testid="role-filter">
                  {[['all', `All (${users.length})`], ['learner', `Learners (${learners.length})`], ['instructor', `Instructors (${instructors.length})`]].map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setRoleFilter(v)}
                      className={`px-3 h-8 ${roleFilter === v ? 'bg-shell-primary text-white font-semibold' : 'bg-white hover:bg-muted'}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              )}
              columns={[
                {
                  key: 'name', header: 'User', accessor: (u) => `${u.first_name} ${u.last_name}`,
                  render: (u) => (
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-shell-primary/10 text-shell-primary flex items-center justify-center text-[11px] font-semibold">
                        {`${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase()}
                      </div>
                      <span className="font-medium">{u.first_name} {u.last_name}</span>
                    </div>
                  ),
                },
                { key: 'contact', header: 'Email / Phone', accessor: (u) => u.email || u.phone || '', className: 'text-muted-foreground' },
                { key: 'role', header: 'Role', width: 120, render: (u) => <StatusBadge value={u.role} /> },
                { key: 'is_active', header: 'Status', width: 170, accessor: (u) => u.is_active !== false, render: (u) => <span className="inline-flex items-center gap-1"><StatusBadge value={u.is_active !== false} />{u.must_change_password && <StatusBadge value="Temp password" tone="warning" />}</span> },
                { key: 'student_level', header: 'Level', width: 120, accessor: (u) => u.student_level || '', hidden: true, className: 'capitalize' },
                { key: 'created_at', header: 'Joined', width: 120, render: (u) => new Date(u.created_at).toLocaleDateString() },
              ]}
              rowTone={(u) => (u.is_active === false ? 'muted' : null)}
              onRowDoubleClick={openEdit}
              primaryActions={(
                <>
                  <Button size="sm" className="h-9" onClick={openCreateUser} data-testid="add-user-btn">
                    <Plus className="w-4 h-4" /> New user
                  </Button>
                  {!isInstructor && (
                    <Button size="sm" variant="outline" className="h-9" onClick={() => openPane(setBulkImportDialogOpen)}>
                      <Upload className="w-4 h-4" /> Bulk import
                    </Button>
                  )}
                </>
              )}
              selectionActions={[
                { key: 'edit', label: 'Edit', icon: PencilSimple, onClick: ([u]) => openEdit(u) },
                { key: 'assign', label: 'Assign courses', icon: Books, requires: 'any', onClick: (rows) => openAssign(rows), disabled: (rows) => rows.some((r) => r.role !== 'learner' && r.role !== 'instructor') },
                { key: 'toggle', label: 'Activate / Deactivate', icon: ToggleRight, onClick: ([u]) => handleToggleStatus(u.id) },
                { key: 'reset', label: 'Reset password', icon: Key, onClick: ([u]) => openReset(u), hidden: isInstructor },
                { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: ([u]) => handleDeleteUser(u.id) },
              ]}
              emptyMessage="No users found"
            />
          </TabsContent>

          <TabsContent value="activity">
            <DataGrid
              title="Recent activity"
              rows={activityLogs}
              loading={loading}
              onRefresh={fetchData}
              selectable={false}
              columns={[
                { key: 'created_at', header: 'When', width: 180, accessor: (l) => l.created_at || l.timestamp, render: (l) => new Date(l.created_at || l.timestamp).toLocaleString() },
                { key: 'action', header: 'Action', width: 160, render: (l) => <StatusBadge value={l.action} tone="neutral" /> },
                { key: 'details', header: 'Details' },
                { key: 'user_id', header: 'By (user id)', width: 160, className: 'font-mono text-xs text-muted-foreground' },
              ]}
              emptyMessage="No activity logs yet"
            />
          </TabsContent>
        </Tabs>
      </Page>
      </SplitLayout>
    </DashboardLayout>
  );
}
